import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDebounceSearch } from "./useDebounceSearch";
import { debounce } from "lodash";
import { startOfDay, endOfDay } from "date-fns";

export interface FetchOptions {
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  [key: string]: any;
}

export interface FetchFilter {
  id: string;
  value: any;
}

type PaginatedResponse<T> =
  | {
      data: T[];
      page: number;
      nextPage: number;
      hasNext: boolean;
      totalPages: number;
      total: number;
    }
  | {
      // schedule shape
      events: any[];
      resources: any[];
    };

type RTKQueryHook<T> = (
  arg: {
    page?: number;
    limit?: number;
    search?: boolean;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: string;
    [key: string]: any;
  },
  options?: { skip?: boolean } & Record<string, any>,
) => {
  data?: PaginatedResponse<T>;
  isUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: unknown;
  refetch?: () => void;
  prefetch?: (arg: any) => void;
};

export interface UseFetchAll<T> {
  queryHook: RTKQueryHook<T>;
  defaultPageSize?: number;
  defaultFilters?: FetchFilter[];
  /**
   * Initial (non-pinned) filters to apply on first render.
   * Useful for deep links (ex: Dashboard -> "View All" with a date range).
   *
   * NOTE: Unlike `defaultFilters`, these are NOT pinned. Clearing filters in the UI
   * should drop back to `defaultFilters` only.
   */
  initialFilters?: FetchFilter[];
  prefetch?: boolean;
  options?: FetchOptions;
}

/**
 * Filter helpers
 *
 * IMPORTANT:
 * Many index pages rely on `defaultFilters` to scope data (ex: clientId, employeeId).
 * Clearing filters in the UI should reset back to that default dataset — not leave
 * stale filtered data or accidentally un-scope the query.
 */
function hasMeaningfulFilterValue(value: any) {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    // Common shapes:
    // - date picker: { start, end }
    // - legacy: { id: "allTime", value: null }
    if ("id" in value && (value as any).id === "allTime") return false;
    if ("value" in value && (value as any).value === null) return false;
    if ("start" in value || "end" in value) {
      return !!(value as any).start || !!(value as any).end;
    }
  }
  return true;
}

function sanitizeFilters(filters: FetchFilter[]) {
  if (!Array.isArray(filters)) return [];
  return filters
    .filter((f) => f && typeof f.id === "string" && f.id.trim().length > 0)
    .filter((f) => hasMeaningfulFilterValue(f.value))
    .map((f) => ({ id: f.id, value: f.value }));
}

function dedupeFiltersByIdKeepLast(filters: FetchFilter[]) {
  if (!Array.isArray(filters) || filters.length === 0) return [];
  const map = new Map<string, FetchFilter>();
  filters.forEach((f) => {
    if (!f?.id) return;
    map.set(f.id, f);
  });
  return Array.from(map.values());
}

function mergeWithBaseFilters(
  incoming: FetchFilter[],
  baseFilters: FetchFilter[],
) {
  const safeIncoming = sanitizeFilters(incoming);
  const safeBase = sanitizeFilters(baseFilters);

  if (safeBase.length === 0) return dedupeFiltersByIdKeepLast(safeIncoming);

  const baseIds = new Set(safeBase.map((f) => f.id));
  const withoutBase = safeIncoming.filter((f) => !baseIds.has(f.id));
  // Base filters are appended last so they remain "pinned" even if incoming tries to clear them.
  return dedupeFiltersByIdKeepLast([...withoutBase, ...safeBase]);
}

function filtersSignature(filters: FetchFilter[]) {
  try {
    // Stable-ish signature used only to detect meaningful changes in defaults.
    // (defaultFilters in callers is often created inline, so reference equality is unreliable)
    return JSON.stringify(
      (Array.isArray(filters) ? filters : []).map((f) => [
        f?.id,
        f?.value,
      ]),
    );
  } catch {
    return String(Date.now());
  }
}

export const useFetchAll = <T,>({
  queryHook,
  defaultPageSize = 20,
  defaultFilters = [],
  initialFilters = [],
  prefetch = true,
  options = {},
}: UseFetchAll<T>) => {
  // Pagination state
  const [{ pageIndex, pageSize }, setPagination] = useState<{
    pageIndex: number;
    pageSize: number;
  }>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Search functionality
  const { search, handleSearch, clearSearch } = useDebounceSearch();

  // Treat defaultFilters as a pinned baseline for the query.
  // This ensures "Clear filters" resets back to the default dataset.
  const baseFilters = useMemo(
    () => sanitizeFilters(defaultFilters),
    // Intentionally *not* using defaultFilters reference directly:
    // many pages pass `defaultFilters: [...]` inline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersSignature(defaultFilters)],
  );

  // Filters state (stores the effective filters = base + user filters)
  const [filters, setFiltersState] = useState<FetchFilter[]>(
    mergeWithBaseFilters(
      Array.isArray(initialFilters) && initialFilters.length > 0
        ? initialFilters
        : defaultFilters,
      baseFilters,
    ),
  );

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Add debounce to filter changes
  const debouncedSetFilters = useCallback(
    debounce((newFilters: FetchFilter[]) => {
      setFiltersState(newFilters);
    }, 300),
    [],
  );

  // Keep effective filters in sync when base filters change (ex: clientId appears later)
  useEffect(() => {
    setFiltersState((prev) => mergeWithBaseFilters(prev, baseFilters));
    // Cancel any pending debounced updates that were based on old defaults
    debouncedSetFilters.cancel?.();
  }, [baseFilters, debouncedSetFilters]);

  // Add request cancellation
  const abortController = useRef<AbortController>(new AbortController());

  useEffect(() => {
    return () => {
      abortController.current.abort();
      debouncedSetFilters.cancel?.();
    };
  }, []);

  // Process filters
  // For date filters, ensure proper start/end of day boundaries using date-fns
  // Adjust for DST variations (PST/UTC-8 vs PDT/UTC-7):
  // - startDate: subtract 1 hour to catch PDT records (07:00 UTC) when filtering in PST
  // - endDate: subtract 1 hour to exclude next day's PDT records (07:00 UTC)
  const searchFilters = filters.reduce<Record<string, any>>((acc, filter) => {
    if (
      filter.id === "transactionDate" ||
      filter.id === "payDate" ||
      filter.id === "dateCreated" ||
      filter.id === "appointmentDate" ||
      filter.id === "dueDate"
    ) {
      let startDate: string | undefined;
      let endDate: string | undefined;

      if (filter.value?.start) {
        const start = startOfDay(filter.value.start);
        // Subtract 1 hour to catch PDT records (UTC-7) when in PST (UTC-8)
        start.setHours(start.getHours() - 1);
        startDate = start.toISOString();
      }

      if (filter.value?.end) {
        const end = endOfDay(filter.value.end);
        // Subtract 1 hour to use PDT boundary, excluding next day's PDT records
        end.setHours(end.getHours() - 1);
        endDate = end.toISOString();
      }

      return {
        ...acc,
        startDate,
        endDate,
      };
    }
    return { ...acc, [filter.id]: filter.value };
  }, {});

  // Build query params
  const queryParams = {
    page: pageIndex + 1,
    limit: pageSize,
    search: search.useSearch,
    searchTerm: search.term,
    sortBy: options.sortBy,
    sortOrder: options.sortOrder,
    ...searchFilters,
  };

  const filteredQueryParams = Object.fromEntries(
    Object.entries(queryParams).filter(
      ([_, value]) => value !== undefined && value !== null && value !== "",
    ),
  );

  // Execute the query with filters as dependency
  const queryResult = queryHook(filteredQueryParams, {
    ...options,
  });

  // Handle global search
  const handleGlobalSearch = useCallback(
    (value: string) => {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      handleSearch(value);
    },
    [handleSearch],
  );

  // Prefetch next page if available
  const prefetchNext = useCallback(() => {
    const paginationData =
      queryResult.data && "hasNext" in queryResult.data ? queryResult.data : null;

    if (
      prefetch &&
      paginationData?.hasNext &&
      paginationData.nextPage <= paginationData.totalPages &&
      queryResult.prefetch
    ) {
      queryResult.prefetch({
        ...filteredQueryParams,
        page: paginationData.nextPage,
      });
    }
  }, [
    prefetch,
    queryResult.data,
    pageSize,
    search.useSearch,
    search.term,
    searchFilters,
    queryResult.prefetch,
    filteredQueryParams,
  ]);

  useEffect(() => {
    prefetchNext();
  }, [prefetchNext]);

  /**
   * Stable identity so callers can safely list `setFilters` in effect dependency arrays
   * without re-running on every render (which would re-apply filters and reset pagination).
   */
  const setFilters = useCallback(
    (next: FetchFilter[] | ((prev: FetchFilter[]) => FetchFilter[])) => {
      const computed =
        typeof next === "function" ? next(filtersRef.current) : next;
      const merged = mergeWithBaseFilters(computed || [], baseFilters);

      // Clearing filters should feel instant and should never leave stale data.
      // Also cancel any pending debounced updates to prevent late "re-applying" old filters.
      const isClearAction = !Array.isArray(computed) || computed.length === 0;
      if (isClearAction) {
        debouncedSetFilters.cancel?.();
        setFiltersState(merged);
        return;
      }

      debouncedSetFilters(merged);
    },
    [baseFilters, debouncedSetFilters],
  );

  // Reset pagination when filters change.
  //
  // NOTE:
  // RTK Query will automatically run a new request when the query args change
  // (which they do when `filters` updates). Manually calling `refetch()` here can
  // cause repeated requests on high-frequency re-renders (ex: Dispatch/Scheduler pages
  // that re-render due to live geolocation updates), which looks like the schedule is
  // "continually reloading".
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [filters]);

  return {
    // Pagination
    pagination: {
      pageIndex,
      pageSize,
      setPagination,
    },
    // Search
    search: {
      handleGlobalSearch,
      clearSearch,
      search,
      value: search.term,
    },
    // Filters
    filters: {
      filters,
      setFilters: (next: FetchFilter[] | ((prev: FetchFilter[]) => FetchFilter[])) => {
        const computed =
          typeof next === "function" ? next(filtersRef.current) : next;
        const merged = mergeWithBaseFilters(computed || [], baseFilters);

        // Clearing filters should feel instant and should never leave stale data.
        // Also cancel any pending debounced updates to prevent late "re-applying" old filters.
        const isClearAction = !Array.isArray(computed) || computed.length === 0;
        if (isClearAction) {
          debouncedSetFilters.cancel?.();
          setFiltersState(merged);
          return;
        }

        debouncedSetFilters(merged);
      },
    },
    // Query results
    queryResult,
    queryParams: filteredQueryParams,
  };
};
